import React, { useCallback, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { IEditorProps } from './Editor';
import { EditorTextField } from './EditorTextField';
import './SuggestStringEditor.less';
import { IconButton } from 'components/buttons/IconButton';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { StringComboBox } from 'components/inputs/StringComboBox';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import { getChildValue, IPropertyChild } from 'components/property-editors/utils/ProviderNodeUtils';
import { debounce } from 'debounce';
import { EditorLabel } from './EditorLabel';
import { useTrackFocus } from 'components/inputs/hooks/useTrackFocus';

interface ISuggestStringEditorProps extends IEditorProps {}

/**
 * This editor allows the user to either free-hand a string constant, or define and maintain (add and edit)
 * a list of string constants from which to choose via drop-down.
 *
 * All list configuration & option selection is design time.
 *
 * If list is not configured in EditorConfigurationValues, render a text-field with an "add" icon button.
 * If a list is present, render a drop/combo, with an 'edit' pencil.
 *
 * Add vs edit are handled differently in different dialogs. The "edit" action also offers the user a chance
 * to add any free-text value to the existing list.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function SuggestStringEditor(props: ISuggestStringEditorProps) {
  const { child, path, onChange } = props;
  const { onShowDialogue, editingContextId } = useContext(PropertyEditorContext);
  const nodeValue = getChildValue(child);
  const [value, setValue] = useState(getChildValue(child));
  const validation = getEditorValidationSummary(child);
  const suggestValueToAdd: boolean = getEditorConfigValue(child, 'suggestValueToAdd');
  const sortValues: boolean = getEditorConfigValue(child, 'sortValues');
  const listName: string = getEditorConfigValue(child, 'suggestedValueListName') || '';
  const propertyName: string = getEditorConfigValue(child, 'propertyName') || '';
  const values = getSuggestStringOptions(child, sortValues);
  const valuesString = values.join(',');
  const [suggestedValues, setSuggestedValues] = useState(values);
  const prevSuggestToAdd = useRef('');

  useEffect(() => {
    if (!isFocused) {
      // if it's focused, don't overwrite with updates from the server
      setValue(nodeValue);
    }
    setSuggestedValues(values);
    // update values if the array has changed.
    // compare string because effect will run with an object equality check.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuesString, nodeValue]);

  const onAddClick = useCallback(() => {
    // TODO put the right options in here:
    onShowDialogue({
      node: child,
      editingContextId: editingContextId,
      path,
      options: { type: 'add', listName, propertyName },
    });
  }, [child, editingContextId, listName, onShowDialogue, path, propertyName]);

  const onEditClick = useCallback(() => {
    onShowDialogue({
      node: child,
      editingContextId: editingContextId,
      path,
      options: { type: 'edit', listName, propertyName },
    });
  }, [child, editingContextId, listName, onShowDialogue, path, propertyName]);

  // can't use the normal debounce hook for this, because of how we need to track the field value to suggest on blur
  const debouncedEmit = useMemo(() => debounce(onChange, 250), [onChange]);

  const handleChange = useCallback(
    (val: string | null) => {
      debouncedEmit.clear();
      if (val === value) {
        return;
      }
      onChange(val, path);
      setValue(val);
    },
    [debouncedEmit, onChange, path, value]
  );

  const handleChangeDebounced = useCallback(
    (val: string | null) => {
      debouncedEmit.clear();
      if (val === value) {
        return;
      }
      debouncedEmit(val, path);
      setValue(val);
    },
    [debouncedEmit, path, value]
  );

  const handleBlur: React.FormEventHandler = useCallback(
    e => {
      handleChange(value); // conditionally emit, right?
      if (
        suggestValueToAdd &&
        suggestedValues.length &&
        !suggestedValues.includes(value) &&
        value !== prevSuggestToAdd.current
      ) {
        onShowDialogue({
          node: child,
          path,
          editingContextId: editingContextId,
          options: { type: 'suggest-to-add-value', value, listName, propertyName },
        });
        prevSuggestToAdd.current = value;
      }
    },
    [
      child,
      editingContextId,
      handleChange,
      listName,
      onShowDialogue,
      path,
      propertyName,
      suggestValueToAdd,
      suggestedValues,
      value,
    ]
  );

  const { focusTrackingProps, isFocused } = useTrackFocus({ onBlur: handleBlur });

  return (
    <EditorLabel editor={child} path={path} hideInputLabel={true}>
      <div className="dpc-suggest-string-editor">
        {listName ? (
          <>
            <StringComboBox
              className="dpc-suggest-string-editor__combo"
              currentValue={value}
              options={suggestedValues}
              validation={validation}
              onValueChange={handleChange}
              onSearchChange={handleChangeDebounced}
              allowFreeText
              allowPartialMatch={false}
              {...focusTrackingProps}
            />
            <IconButton
              title="Edit Suggestion List"
              className="dpc-suggest-string-editor__button"
              iconClassName={'dpc-suggest-string-editor__edit-icon'}
              iconSize="small"
              onClick={onEditClick}
            />
          </>
        ) : (
          <>
            <EditorTextField {...props} /> {/* pass all normal change handling through */}
            <IconButton
              title="Add suggestion list"
              className="dpc-suggest-string-editor__button"
              iconClassName={'dpc-suggest-string-editor__add-icon'}
              iconSize="small"
              onClick={onAddClick}
            />
          </>
        )}
      </div>
    </EditorLabel>
  );
}

function getSuggestStringOptions(node: IPropertyChild, sort = false) {
  let values: string[] = getEditorConfigValue(node, 'suggestedValues') || [];
  if (sort) {
    values = values.sort();
  }
  return values;
}
